import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, ShieldCheck, Smartphone, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Public page (opened in the phone's browser, e.g. via QR from the app): registers THIS browser as a team test
// device — every scan of a printed QR code from it is a test scan, never a real one.
export default function TestDevicePage() {
  const params = new URLSearchParams(window.location.search);
  const [name, setName] = useState(params.get('name') || '');
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = () => axios.get(`${API}/api/public/tester`).then(r => setStatus(r.data)).catch(() => setStatus({ registered: false }));
  useEffect(() => { load(); }, []);

  const register = async (n) => {
    setBusy(true); setError('');
    try { const r = await axios.post(`${API}/api/public/tester`, { name: n }); setStatus(r.data); }
    catch (err) { setError(err.response?.data?.detail || 'Registrierung fehlgeschlagen'); }
    finally { setBusy(false); }
  };
  const unregister = async () => {
    setBusy(true);
    try { await axios.delete(`${API}/api/public/tester`); await load(); }
    finally { setBusy(false); }
  };
  // auto-register when opened via the app's QR/link (?name=…)
  useEffect(() => {
    if (status && !status.registered && params.get('name') && !busy && !error) register(params.get('name'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" style={{ background: '#0A141F' }} data-testid="test-device-page">
      <div className="text-slate-100 font-bold text-2xl tracking-wide">OLTA<span className="block text-[10px] tracking-[0.35em] font-normal text-slate-400">NACHFOLGE</span></div>
      <div className="mt-8 w-full max-w-sm rounded-md border border-slate-700/60 bg-slate-900/60 p-5 space-y-4">
        {!status ? <Loader2 className="w-5 h-5 text-sky-500 animate-spin mx-auto" /> : status.registered ? (
          <>
            <ShieldCheck className="w-8 h-8 text-emerald-400 mx-auto" />
            <p className="text-slate-100 text-base" data-testid="test-device-registered">
              Dieses Gerät ist als <span className="font-semibold">Test-Gerät von {status.name}</span> registriert.
            </p>
            <p className="text-slate-400 text-sm">{status.device} — jeder QR-Scan aus diesem Browser zählt als Test-Scan, nie als echter Scan (gilt 1 Jahr, nur in diesem Browser).</p>
            <button onClick={unregister} disabled={busy} data-testid="test-device-unregister"
              className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200 border border-slate-700 rounded px-3 py-1.5 disabled:opacity-50">
              <X className="w-3.5 h-3.5" /> Registrierung aufheben
            </button>
          </>
        ) : (
          <>
            <Smartphone className="w-8 h-8 text-sky-400 mx-auto" />
            <p className="text-slate-100 text-base">Dieses Gerät als Test-Gerät registrieren</p>
            <p className="text-slate-400 text-sm">Danach zählt jeder QR-Scan aus diesem Browser als Test — egal ob der Brief schon versendet ist.</p>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Dein Name (wie im Team)" data-testid="test-device-name"
              className="w-full h-10 px-3 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm" />
            {error && <p className="text-rose-300 text-xs" data-testid="test-device-error">{error}</p>}
            <button onClick={() => register(name)} disabled={busy || !name.trim()} data-testid="test-device-register"
              className="w-full h-10 rounded bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Registrieren'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
